
import PSPDFKit from "pspdfkit";
import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  ApiManager,
  sessionService,
  ApiEndpoints,
  EnumUtility,
  DocumentUtility,
  SessionManager,
  batchDataService,
} from '../components/ComponentsLibrary';
import SignalCellularNullTwoTone from "@mui/icons-material/SignalCellularNullTwoTone";
import { Api } from "@mui/icons-material";
import LinkIcon from '@mui/icons-material/Link';
 
const PdfViewerComponent = forwardRef((props, ref) => {
  // Define the static function
  PdfViewerComponent.getDocViewerLicenseKey = () => {
    return SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.DocViewerLicenseKey || "";
  };
  const {
    origin,
    viewerAccessPermissions,
    onSplitButtonPress = () => { },
    onSplitAllButtonPress = () => { },
    onSaveRotateButtonPress = () => { },
    onSaveAsButtonPress  = () => { },      //MJQ3
    onDeleteButtonPress = () => { },
    onPDFReadError = () => { },
    onCopyPathButtonPress = () => { },
    onEditorCloseRequest = () => true,
    persistViewerPreferences = false,
    viewerPreferencesSessionKey = "IndexViewerPreferences"
  } = props; 

  const persistViewPreferences = !!persistViewerPreferences;

  const getPersistedViewerPreferences = () => {
    try {
      if (!persistViewPreferences) {
        return { thumbnailPanelActive: false, fitToPageActive: false };
      }

      const persistedValue = sessionStorage.getItem(viewerPreferencesSessionKey);
      if (!persistedValue) {
        return { thumbnailPanelActive: false, fitToPageActive: false };
      }

      const parsedValue = JSON.parse(persistedValue);
      return {
        thumbnailPanelActive: !!parsedValue?.thumbnailPanelActive,
        fitToPageActive: !!parsedValue?.fitToPageActive
      };
    }
    catch (error) {
      return { thumbnailPanelActive: false, fitToPageActive: false };
    }
  };

  const setPersistedViewerPreferences = (preferences) => {
    try {
      if (!persistViewPreferences) {
        return;
      }

      sessionStorage.setItem(viewerPreferencesSessionKey, JSON.stringify({
        thumbnailPanelActive: !!preferences?.thumbnailPanelActive,
        fitToPageActive: !!preferences?.fitToPageActive
      }));
    }
    catch (error) {
      // Ignore persistence failures and keep viewer functional.
    }
  };

  const applyPersistedViewerPreferences = (viewerInstance, PSPDFKitRef) => {
    if (!persistViewPreferences || !viewerInstance || !PSPDFKitRef) {
      return;
    }

    const persistedPreferences = getPersistedViewerPreferences();

    viewerInstance.setViewState((viewState) => {
      let updatedViewState = viewState;
      const sidebarMode = persistedPreferences.thumbnailPanelActive
        ? PSPDFKitRef.SidebarMode.THUMBNAILS
        : null;
      const zoomMode = persistedPreferences.fitToPageActive
        ? PSPDFKitRef.ZoomMode.FIT_TO_VIEWPORT
        : PSPDFKitRef.ZoomMode.AUTO;

      if (updatedViewState.get("sidebarMode") !== sidebarMode) {
        updatedViewState = updatedViewState.set("sidebarMode", sidebarMode);
      }

      if (updatedViewState.get("zoomMode") !== zoomMode) {
        updatedViewState = updatedViewState.set("zoomMode", zoomMode);
      }

      return updatedViewState;
    });
  };

  const registerViewerPreferenceListener = (viewerInstance, PSPDFKitRef) => {
    if (!persistViewPreferences || !viewerInstance || !PSPDFKitRef) {
      return;
    }

    viewerInstance.addEventListener("viewState.change", (viewState) => {
      const isThumbnailPanelActive = viewState.get("sidebarMode") === PSPDFKitRef.SidebarMode.THUMBNAILS;
      const isFitToPageActive = viewState.get("zoomMode") === PSPDFKitRef.ZoomMode.FIT_TO_VIEWPORT;

      setPersistedViewerPreferences({
        thumbnailPanelActive: isThumbnailPanelActive,
        fitToPageActive: isFitToPageActive
      });
    });
  };
 
  const copyPathButtonImage = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M17 7H13V9H17C18.65 9 20 10.35 20 12C20 13.65 18.65 15 17 15H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7ZM11 15H7C5.35 15 4 13.65 4 12C4 10.35 5.35 9 7 9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15ZM8 11H16V13H8V11Z" fill="currentColor"/>
</svg>`;
 
  const copyPathButton = {
    type: "custom",
    id: "custom-copyButton-id",
    title: "Copy Path",
    disabled: false,
    followsPageSelection: true,
    onPress: onCopyPathButtonPress && onCopyPathButtonPress,
    className: "oom8bse", // Add custom class name,
    icon: copyPathButtonImage
  };

  const splitButton = {
    type: "custom",
    id: "custom-splitButton-id",
    title: "Split",
    disabled: true,
    followsPageSelection: true,
    onPress: (event, context) => {
      if (context?.getSelectedPageIndexes) pspdfkitGetSelectedRef.current = context.getSelectedPageIndexes;
      onSplitButtonPress && onSplitButtonPress(event, context);
    },
    className: "oom8bse" // Add custom class name
  };

  // Split All operates on the whole document rather than a page selection,
  // so it never needs to inspect or preset the editor's page selection.
  const splitAllButton = {
    type: "custom",
    id: "custom-splitAllButton-id",
    title: "Split All",
    disabled: false,
    followsPageSelection: false,
    onPress: (event, context) => {
      onSplitAllButtonPress && onSplitAllButtonPress(event, context);
    },
    className: "oom8bse"
  };
 
  // const node = instance.contentDocument.createElement("button");
  // node.innerHTML = "Save Rotate";
  const saveRotateButton = {
    type: "custom",
    id: "custom-rotateButton-id",
    title: "Save",
    followsPageSelection: false,
    onPress: (event, context) => {
      if (context?.getSelectedPageIndexes) pspdfkitGetSelectedRef.current = context.getSelectedPageIndexes;
      onSaveRotateButtonPress && onSaveRotateButtonPress(event, context);
    },
    className: "oom8bse" // Add custom class name
  };

  const deleteButton = {
    type: "custom",
    id: "custom-deleteButton-id",
    title: "Delete",
    disabled: true,
    followsPageSelection: true,
    onPress: (event, context) => {
      if (context?.getSelectedPageIndexes) pspdfkitGetSelectedRef.current = context.getSelectedPageIndexes;
      onDeleteButtonPress && onDeleteButtonPress(event, context);
    },
    className: "oom8bse" // Add custom class name
  };

  const saveAsButtonImageUrl = '/images/SaveIcon.svg';   //MJQ3

  const saveAsButton = {    //MJQ3
    type: "custom",
    id: "custom-saveAsButton-id",
    title: "Save As",
    disabled: false,
    followsPageSelection: false,
    onPress: onSaveAsButtonPress && onSaveAsButtonPress,
    size: 24,
    // icon: '<svg><path d="/images/SaveIcon.svg"/></svg>'
    //icon: `<svg><path d='${saveAsButtonImageUrl}'/></svg>`    
    //icon: `url('${saveAsButtonImageUrl}')` // Path to the icon    
  };

  const containerRef = useRef(null);
  const allowEditorCloseRef = useRef(false);
  const lastEditorSelectedCountRef = useRef(0);
  const editorSelectionObserverRef = useRef(null);
  const pspdfkitGetSelectedRef = useRef(null);
  let instance = null;

  let editorAccess = true, editorToolbarAccess = true, rotateButtonAccess = true, splitButtonAccess = true, deleteButtonAccess = true, saveButtonAccess = true;
  const viewOnly = viewerAccessPermissions ? (viewerAccessPermissions.viewOnlyAccess ? viewerAccessPermissions.viewOnlyAccess : false) : false;

  if (viewOnly) {
    splitButtonAccess = false; deleteButtonAccess = false;
  }
  else {
    editorAccess = viewerAccessPermissions ? !(viewerAccessPermissions.editorAccess?.hide ?? false) : true;
    editorToolbarAccess = viewerAccessPermissions ? !(viewerAccessPermissions.viewerToolbarItemsAccess?.hide ?? false) : true;
    rotateButtonAccess = viewerAccessPermissions ? !(viewerAccessPermissions.rotateButtonAccess?.hide ?? false) : true;
    splitButtonAccess = viewerAccessPermissions ? !(viewerAccessPermissions.splitButtonAccess?.hide ?? false) : true;
    deleteButtonAccess = viewerAccessPermissions ? !(viewerAccessPermissions.deleteButtonAccess?.hide ?? false) : true;
    saveButtonAccess = viewerAccessPermissions ? !(viewerAccessPermissions.saveButtonAccess?.hide ?? false) : true;
  }

  const customEditorToolbarItems = [];

  if (rotateButtonAccess) {
    customEditorToolbarItems.push({ type: "rotate-left" });
    customEditorToolbarItems.push({ type: "rotate-right" });
    saveButtonAccess && customEditorToolbarItems.push(saveRotateButton);
  };

  if (splitButtonAccess) {
    customEditorToolbarItems.push(splitButton);
    customEditorToolbarItems.push(splitAllButton);
  };

  if (deleteButtonAccess) { customEditorToolbarItems.push(deleteButton); };


  //const origin = useState(props.origin); // This is the origin of the document being displayed. It is either CaseInformation or DocumentSearch.

  const closeDocumentEditor = (viewerInstance = instance) => {
    if (!viewerInstance) {
      return;
    }

    allowEditorCloseRef.current = true;
    viewerInstance.setViewState((viewState) => viewState.set("interactionMode", null));
  };

  const isDocumentEditorOpen = (viewerInstance = instance) => {
    if (!viewerInstance?.viewState?.get) {
      return false;
    }

    try {
      return viewerInstance.viewState.get("interactionMode") === PSPDFKit.InteractionMode.DOCUMENT_EDITOR;
    }
    catch (_) {
      return false;
    }
  };

  const getDocumentEditorSelectedPageCount = (viewerInstance = instance) => {
  if (!viewerInstance || !isDocumentEditorOpen(viewerInstance)) {
    return 0;
  }

  // PSPDFKit's own getSelectedPageIndexes captured from toolbar onPress — same source as Split button
  if (pspdfkitGetSelectedRef.current) {
    try {
      const selected = pspdfkitGetSelectedRef.current();
      if (Array.isArray(selected)) return selected.length;
    } catch (_) {}
  }

  try {
    // PSPDFKit instance method fallback
    if (typeof viewerInstance.getSelectedPageIndexes === 'function') {
      const selected = viewerInstance.getSelectedPageIndexes();
      if (Array.isArray(selected)) return selected.length;
    }
  }
  catch (_) {}

  const contentRoot = viewerInstance.contentDocument;
  if (!contentRoot) {
    return 0;
  }

  // Scope query to the document editor section to avoid matching unrelated selected elements
  const docEditSection = contentRoot.querySelector('section.PSPDFKit-Modal-Dialog');
  const queryRoot = docEditSection || contentRoot;
  // Try both attributes — PSPDFKit versions differ on which they use
  const explicitlySelectedEls = queryRoot.querySelectorAll('[data-selected="true"], [aria-selected="true"]');

  return explicitlySelectedEls.length;
};

  const syncDocumentEditorSelectionState = (viewerInstance = instance) => {
    if (!isDocumentEditorOpen(viewerInstance)) {
      lastEditorSelectedCountRef.current = 0;
      return;
    }

    lastEditorSelectedCountRef.current = getDocumentEditorSelectedPageCount(viewerInstance);
  };

  const attachDocumentEditorSelectionObserver = (viewerInstance, contentRoot, docEditDiv) => {
    if (editorSelectionObserverRef.current || !contentRoot || !docEditDiv) {
      return;
    }

    const observer = new MutationObserver(() => {
      syncDocumentEditorSelectionState(viewerInstance);
    });

    observer.observe(contentRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-selected", "disabled", "aria-disabled", "class"]
    });

    editorSelectionObserverRef.current = observer;
    syncDocumentEditorSelectionState(viewerInstance);
  };

  const detachDocumentEditorSelectionObserver = () => {
    if (editorSelectionObserverRef.current) {
      editorSelectionObserverRef.current.disconnect();
      editorSelectionObserverRef.current = null;
    }
  };

 const requestDocumentEditorClose = (viewerInstance = instance, knownSelectionCount = null) => {
  if (!viewerInstance) {
    return;
  }

  let selectedPageCount = knownSelectionCount;

  if (selectedPageCount === null) {
    selectedPageCount = getDocumentEditorSelectedPageCount(viewerInstance);
  }

  const hasSelectedPages = selectedPageCount > 0;

  if (!hasSelectedPages) {
    closeDocumentEditor(viewerInstance);
    return;
  }

  // Inject a dialog into the PSPDFKit iframe so focus never leaves the iframe
  // and PSPDFKit page selections are preserved.
  showInlineEditorCloseConfirm(viewerInstance);
};

  const ensureEditorCloseButton = (viewerInstance, PSPDFKitRef) => {
    const contentDoc = viewerInstance?.contentDocument;
    if (!contentDoc) {
      return;
    }

    const contentRoot = contentDoc;
    const domDocument = contentDoc.nodeType === 9 ? contentDoc : (contentDoc.ownerDocument || document);

    const docEditDiv = contentRoot.querySelector('section.PSPDFKit-Modal-Dialog') ||
      contentRoot.querySelector('section.cjv3et0.PSPDFKit-Modal-Dialog');
    if (!docEditDiv) {
      return;
    }

    attachDocumentEditorSelectionObserver(viewerInstance, contentRoot, docEditDiv);

    const backdropGuardHost = contentRoot.body || contentRoot.documentElement || docEditDiv.parentElement || docEditDiv;

    if (!backdropGuardHost.dataset.ytEditorBackdropGuardAttached) {
      backdropGuardHost.dataset.ytEditorBackdropGuardAttached = "true";
      backdropGuardHost.addEventListener("pointerdown", (event) => {
        if (!docEditDiv.contains(event.target)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      backdropGuardHost.addEventListener("click", (event) => {
        if (!docEditDiv.contains(event.target)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    }

    if (!docEditDiv.dataset.ytCloseGuardAttached) {
      docEditDiv.dataset.ytCloseGuardAttached = "true";
      docEditDiv.addEventListener("pointerdown", (event) => {
        if (event.target === docEditDiv) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        const nativeCloseButton = event.target?.closest(
          "button[aria-label='Close'], button[title='Close'], button[data-testid='close-button']"
        );

        if (nativeCloseButton && nativeCloseButton.id !== "yt-doc-editor-close-btn") {
          event.preventDefault();
          event.stopPropagation();
          const count = getDocumentEditorSelectedPageCount(viewerInstance);
          void requestDocumentEditorClose(viewerInstance, count);
        }
      }, true);

      docEditDiv.addEventListener("mousedown", (event) => {
        const nativeCloseButton = event.target?.closest(
          "button[aria-label='Close'], button[title='Close'], button[data-testid='close-button']"
        );

        if (nativeCloseButton && nativeCloseButton.id !== "yt-doc-editor-close-btn") {
          event.preventDefault();
          event.stopPropagation();
          const count = getDocumentEditorSelectedPageCount(viewerInstance);
          void requestDocumentEditorClose(viewerInstance, count);
        }
      }, true);

      docEditDiv.addEventListener("pointerup", (event) => {
        const nativeCloseButton = event.target?.closest(
          "button[aria-label='Close'], button[title='Close'], button[data-testid='close-button']"
        );

        if (nativeCloseButton && nativeCloseButton.id !== "yt-doc-editor-close-btn") {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      docEditDiv.addEventListener("click", (event) => {
        if (event.target === docEditDiv) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      docEditDiv.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    }

    const nativeCloseButton = docEditDiv.querySelector(
      "button[aria-label='Close'], button[title='Close'], button[data-testid='close-button']"
    );

    const customCloseButton = contentRoot.querySelector("#yt-doc-editor-close-btn");

    if (nativeCloseButton) {
  const cleanCloseButton = nativeCloseButton.cloneNode(true);
  nativeCloseButton.parentNode.replaceChild(cleanCloseButton, nativeCloseButton);

  const interceptCloseButtonEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const count = getDocumentEditorSelectedPageCount(viewerInstance);
    requestDocumentEditorClose(viewerInstance, count);
  };

  cleanCloseButton.style.display = "";
  cleanCloseButton.style.visibility = "visible";
  cleanCloseButton.style.opacity = "1";
  cleanCloseButton.style.border = "none";
  cleanCloseButton.style.background = "transparent";
  cleanCloseButton.style.boxShadow = "none";
  cleanCloseButton.style.outline = "none";
  cleanCloseButton.style.padding = "0";

  cleanCloseButton.addEventListener("pointerdown", interceptCloseButtonEvent, true);
  cleanCloseButton.addEventListener("mousedown", interceptCloseButtonEvent, true);
  cleanCloseButton.addEventListener("click", interceptCloseButtonEvent, true);

  if (customCloseButton) {
    customCloseButton.remove();
  }

  return;
}

    if (!customCloseButton) {
      const closeButton = domDocument.createElement("button");
      closeButton.id = "yt-doc-editor-close-btn";
      closeButton.type = "button";
      closeButton.title = "Close";
      closeButton.setAttribute("aria-label", "Close");
      closeButton.textContent = "\u00d7";
      closeButton.style.position = "absolute";
      closeButton.style.top = "4px";
      closeButton.style.right = "10px";
      closeButton.style.width = "30px";
      closeButton.style.height = "30px";
      closeButton.style.border = "none";
      closeButton.style.borderRadius = "0";
      closeButton.style.background = "transparent";
      closeButton.style.boxShadow = "none";
      closeButton.style.outline = "none";
      closeButton.style.color = "#5e5e5e";
      closeButton.style.cursor = "pointer";
      closeButton.style.zIndex = "1000";
      closeButton.style.lineHeight = "1";
      closeButton.style.fontSize = "34px";
      closeButton.style.fontWeight = "400";
      const interceptCloseButtonEvent = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const count = getDocumentEditorSelectedPageCount(viewerInstance);
        void requestDocumentEditorClose(viewerInstance, count);
      };
      closeButton.addEventListener("pointerdown", interceptCloseButtonEvent, true);
      closeButton.addEventListener("mousedown", interceptCloseButtonEvent, true);
      closeButton.addEventListener("mouseup", (event) => {
        event.preventDefault();
        event.stopPropagation();
      }, true);
      closeButton.addEventListener("pointerup", (event) => {
        event.preventDefault();
        event.stopPropagation();
      }, true);
      closeButton.addEventListener("click", interceptCloseButtonEvent, true);
      closeButton.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          interceptCloseButtonEvent(event);
        }
      }, true);
      docEditDiv.appendChild(closeButton);
    }
  };

  const handleDocumentEditorStateChange = (viewerInstance, PSPDFKitRef, viewState, previousViewState) => {
    const enteredEditor = viewState.get("interactionMode") === PSPDFKitRef.InteractionMode.DOCUMENT_EDITOR;
    const exitedEditor = previousViewState?.get("interactionMode") === PSPDFKitRef.InteractionMode.DOCUMENT_EDITOR
      && viewState.get("interactionMode") !== PSPDFKitRef.InteractionMode.DOCUMENT_EDITOR;

    if (enteredEditor) {
      lastEditorSelectedCountRef.current = 0;
      pspdfkitGetSelectedRef.current = null; // Reset for new editor session
      setTimeout(() => {
        const parentDiv = containerRef.current || document.querySelector("div.docViewerForm");
        const docEditDiv = viewerInstance.contentDocument.querySelector('section.PSPDFKit-Modal-Dialog') ||
          viewerInstance.contentDocument.querySelector('section.cjv3et0.PSPDFKit-Modal-Dialog');

        if (docEditDiv && parentDiv) {
          const parentRect = parentDiv.getBoundingClientRect();
          // top offset must be subtracted from height too, or the shifted box overflows the panel by that amount
          const topOffset = 10;
          const bottomMargin = 10;
          const newChildWidth = parentRect.width - 50;
          const newChildHeight = parentRect.height - (topOffset + bottomMargin);

          docEditDiv.style.position = "relative";
          docEditDiv.style.width = `${newChildWidth}px`;
          docEditDiv.style.maxWidth = `${newChildWidth}px`;
          docEditDiv.style.height = `${newChildHeight}px`;
          docEditDiv.style.maxHeight = `${newChildHeight}px`;
          docEditDiv.style.overflowY = "auto";
          docEditDiv.style.top = `${topOffset}px`;
        }

        ensureEditorCloseButton(viewerInstance, PSPDFKitRef);
        syncDocumentEditorSelectionState(viewerInstance);
      }, 10);
      return;
    }

    if (exitedEditor) {
      detachDocumentEditorSelectionObserver();
      lastEditorSelectedCountRef.current = 0;
      if (allowEditorCloseRef.current) {
        // Close was triggered by our closeDocumentEditor — allow it through
        allowEditorCloseRef.current = false;
      }
      else {
        // Unexpected close (e.g. focus stolen by an external dialog) — reopen the editor
        allowEditorCloseRef.current = false;
        try {
          viewerInstance.setViewState(vs => vs.set("interactionMode", PSPDFKitRef.InteractionMode.DOCUMENT_EDITOR));
        } catch (_) {
          // If reopen fails, silently allow the close
        }
      }
    }
  };

  async function handleDocumentExport(arrayBuffer, mainPdfInstance) {
    // exits Document Editor programmatically
    closeDocumentEditor(mainPdfInstance);
    //you can do the below if you want to export the unmodified main file
    //and do with the buffer whatever you want here. Send it to a server, download, etc.
    // const mainPdfFileArrayBuffer = await mainPdfInstance.exportPDF()

    //unloads the main document
    PSPDFKit.unload(mainPdfInstance)

    //loads the modified file
    instance = await PSPDFKit.load({
      isEditableAnnotation: function (ann) {
        return !(ann instanceof PSPDFKit.Annotations.WidgetAnnotation);
      },
      licenseKey: PdfViewerComponent.getDocViewerLicenseKey(), 
      theme: PSPDFKit.Theme.AUTO,
      container: containerRef?.current,
      document: arrayBuffer,
      printOptions: {
        mode: PSPDFKit.PrintMode.DOM,
        quality: PSPDFKit.PrintQuality[
          (SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.DocViewerPrintQuality || "HIGH").toUpperCase()
        ] || PSPDFKit.PrintQuality.HIGH
      },
      toolbarItems: getToolbarItems(PSPDFKit),
      
      baseUrl: `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`
      //"https://cdn.cloud.pspdfkit.com/pspdfkit-web@2024.8.1/"      
    });

    instance.setDocumentEditorToolbarItems(
      editorToolbarAccess ?
        customEditorToolbarItems : []
    );

    applyPersistedViewerPreferences(instance, PSPDFKit);
    registerViewerPreferenceListener(instance, PSPDFKit);

    instance.addEventListener("viewState.change", (viewState, previousViewState) => {
      handleDocumentEditorStateChange(instance, PSPDFKit, viewState, previousViewState);
    });

    // Track selected page count while the document editor is open
    try {
      instance.addEventListener("documentEditor.selectedPageIndexes.change", (selectedPageIndexes) => {
        const count = Array.isArray(selectedPageIndexes) ? selectedPageIndexes.length : 0;
        lastEditorSelectedCountRef.current = count > 0 ? count : 0;
      });
    } catch (_) { /* event not supported in this PSPDFKit version */ }

    instance.setDocumentEditorFooterItems([]);  // Hide the footer toolbar items

    batchDataService.updateDocSplitData({ batchInstance: instance });
  }

  const getToolbarItems = (PSPDFKit) => {
    const items = [...PSPDFKit.defaultToolbarItems.filter((item) => (
      // Example condition: remove items with type 'search'
      item.type === 'sidebar-thumbnails' || item.type === 'pager' || item.type === 'zoom-out'
      || item.type === 'zoom-in' || item.type === 'zoom-mode' || item.type === 'print' ||
      editorAccess && item.type === 'document-editor'
    )), saveAsButton]
    const tools = [];
    items.forEach(item => {
      tools.push(item)
      if (item.type === 'print') { tools.push(copyPathButton) }
    });
    return tools
  }


  async function handleDocumentExport(arrayBuffer, mainPdfInstance) {
    // exits Document Editor programmatically
    closeDocumentEditor(mainPdfInstance);
    //you can do the below if you want to export the unmodified main file
    //and do with the buffer whatever you want here. Send it to a server, download, etc.
    // const mainPdfFileArrayBuffer = await mainPdfInstance.exportPDF()

    //unloads the main document
    PSPDFKit.unload(mainPdfInstance)

    //loads the modified file
    instance = await PSPDFKit.load({
      isEditableAnnotation: function (ann) {
        return !(ann instanceof PSPDFKit.Annotations.WidgetAnnotation);
      },
      licenseKey: PdfViewerComponent.getDocViewerLicenseKey(), 
      theme: PSPDFKit.Theme.AUTO,
      container: containerRef?.current,
      document: arrayBuffer,
      //toolbarItems: [...PSPDFKit.defaultToolbarItems, {type: "comment"}],
      toolbarItems: getToolbarItems(PSPDFKit),
      
      baseUrl: `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`
      //"https://cdn.cloud.pspdfkit.com/pspdfkit-web@2024.8.1/"      
    });

    instance.setDocumentEditorToolbarItems(
      editorToolbarAccess ?
        customEditorToolbarItems : []
    );

    applyPersistedViewerPreferences(instance, PSPDFKit);
    registerViewerPreferenceListener(instance, PSPDFKit);

    instance.addEventListener("viewState.change", (viewState, previousViewState) => {
      handleDocumentEditorStateChange(instance, PSPDFKit, viewState, previousViewState);
    });

    // Track selected page count while the document editor is open
    try {
      instance.addEventListener("documentEditor.selectedPageIndexes.change", (selectedPageIndexes) => {
        const count = Array.isArray(selectedPageIndexes) ? selectedPageIndexes.length : 0;
          lastEditorSelectedCountRef.current = count > 0 ? count : 0;
      });
    } catch (_) { /* event not supported in this PSPDFKit version */ }

    instance.setDocumentEditorFooterItems([]);  // Hide the footer toolbar items

    batchDataService.updateDocSplitData({ batchInstance: instance });
  }

  


  useEffect(() => {
    const container = containerRef.current;
    //let instance = null;
    let PSPDFKit;

    const initializePSPDFKit = async () => {
      try {
        PSPDFKit = await import("pspdfkit");

        if (container) {
          PSPDFKit.unload(container);
        }
        //console.log('props.document:', props.document);
        instance = await PSPDFKit.load({
          isEditableAnnotation: function (ann) {
            return !(ann instanceof PSPDFKit.Annotations.WidgetAnnotation);
          },
          licenseKey: PdfViewerComponent.getDocViewerLicenseKey(),
          theme: PSPDFKit.Theme.AUTO,
          container,
          document: props.document,
          printOptions: {
            mode: PSPDFKit.PrintMode.DOM,
            quality: PSPDFKit.PrintQuality[
              (SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.DocViewerPrintQuality || "HIGH").toUpperCase()
            ] || PSPDFKit.PrintQuality.HIGH
          },
          toolbarItems: getToolbarItems(PSPDFKit),
          baseUrl: `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`
          //"https://cdn.cloud.pspdfkit.com/pspdfkit-web@2024.8.1/"
        });


        instance.setDocumentEditorToolbarItems(
          editorToolbarAccess ?
            customEditorToolbarItems : []
        );

        applyPersistedViewerPreferences(instance, PSPDFKit);
        registerViewerPreferenceListener(instance, PSPDFKit);
        

        instance.addEventListener("viewState.change", (viewState, previousViewState) => {
          handleDocumentEditorStateChange(instance, PSPDFKit, viewState, previousViewState);
        });

        // Track selected page count while the document editor is open
        try {
          instance.addEventListener("documentEditor.selectedPageIndexes.change", (selectedPageIndexes) => {
            const count = Array.isArray(selectedPageIndexes) ? selectedPageIndexes.length : 0;
            lastEditorSelectedCountRef.current = count > 0 ? count : 0;
          });
        } catch (_) { /* event not supported in this PSPDFKit version */ }


        // Update button state when selection changes
        /* instance.addEventListener('selection.change', () => {
          const selectedPages = instance.getSelectedPageIndexes();
          const hasMultipleSelection = selectedPages.length > 1;
          instance.setDocumentEditorToolbarItems(items =>
            items.map(item => {
              switch (item.id) {
                case 'custom-deleteButton-id':
                  return { ...item, disabled: !hasMultipleSelection }; // Only enable for multiple selections
                case 'custom-splitButton-id':
                  return { ...item, disabled: !hasMultipleSelection }; // Only enable for multiple selections
                default:
                  return item;
              }
            })
          );
        }); */


        instance.setDocumentEditorFooterItems([]);  // Hide the footer toolbar items

        batchDataService.updateDocSplitData({ batchInstance: instance });
      } catch (error) {
        let errorMessage =  "Unexpected error occured while reading the file" 
        if (error.message.includes("File not in PDF format or corrupted")) {
          errorMessage =  "Corrupted PDF file" 
        }
        else if (error.message.includes("Unable to create temporary document: libtiff unable to open TIFF image")) {
          errorMessage =  "Corrupted TIF file" 
        }
        onPDFReadError(errorMessage);
        }
    };

    initializePSPDFKit();

    return () => {
      detachDocumentEditorSelectionObserver();
      if (PSPDFKit && container) {
        PSPDFKit.unload(container);
      }
    };
  }, [props.document]);

  const showInlineEditorCloseConfirm = (viewerInstance) => {
    const contentDoc = viewerInstance?.contentDocument;
    if (!contentDoc) {
      return;
    }

    // contentDoc may be a DocumentFragment/Element rather than a Document.
    // Use the same pattern as ensureEditorCloseButton to get a real Document.
    const domDocument = contentDoc.nodeType === 9 ? contentDoc : (contentDoc.ownerDocument || document);

    if (contentDoc.querySelector('#yt-editor-close-overlay')) {
      return;
    }

    // Append inside the PSPDFKit modal dialog section so that the existing
    // backdrop-guard listener (which blocks clicks outside docEditDiv) does
    // not swallow our button clicks.
    const docEditDiv =
      contentDoc.querySelector('section.PSPDFKit-Modal-Dialog') ||
      contentDoc.querySelector('section.cjv3et0.PSPDFKit-Modal-Dialog') ||
      contentDoc.body ||
      contentDoc.documentElement;

    const overlay = domDocument.createElement('div');
    overlay.id = 'yt-editor-close-overlay';
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:99999',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(0,0,0,0.5)',
    ].join(';');

    const dialog = domDocument.createElement('div');
    dialog.style.cssText = [
      'background:#EF6C00',
      'border-radius:4px',
      'padding:20px 24px 16px',
      'max-width:460px',
      'width:90%',
      'box-shadow:0 11px 15px rgba(0,0,0,0.2)',
      'font-family:Roboto,sans-serif',
      'position:relative',
      'color:#fff',
    ].join(';');

    const closeX = domDocument.createElement('button');
    closeX.type = 'button';
    closeX.textContent = '\u00d7';
    closeX.style.cssText = [
      'position:absolute',
      'top:4px',
      'right:8px',
      'border:none',
      'background:transparent',
      'font-size:30px',
      'line-height:1',
      'cursor:pointer',
      'color:#fff',
      'padding:0',
    ].join(';');

    const title = domDocument.createElement('p');
    title.style.cssText = 'margin:0 0 8px;font-size:1rem;font-weight:600;color:#fff;';
    title.textContent = 'Warning';

    const message = domDocument.createElement('p');
    message.style.cssText = 'margin:0 0 20px;font-size:0.875rem;color:#fff;';
    message.textContent = 'Any unsaved changes will be lost. Are you sure you want to proceed?';

    const btnRow = domDocument.createElement('div');
    btnRow.style.cssText = 'display:flex;justify-content:center;gap:45px;';

    const btnStyle = [
      'padding:6px 16px',
      'border-radius:4px',
      'border:2px solid #fff',
      'cursor:pointer',
      'background:transparent',
      'color:#fff',
      'font-size:0.875rem',
      'font-weight:500',
      'letter-spacing:0.02857em',
    ].join(';');

    const removeOverlay = () => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };

    const yesBtn = domDocument.createElement('button');
    yesBtn.type = 'button';
    yesBtn.textContent = 'Yes';
    yesBtn.style.cssText = btnStyle;
    yesBtn.addEventListener('click', () => {
      removeOverlay();
      closeDocumentEditor(viewerInstance);
    });

    const noBtn = domDocument.createElement('button');
    noBtn.type = 'button';
    noBtn.textContent = 'No';
    noBtn.style.cssText = btnStyle;
    noBtn.addEventListener('click', removeOverlay);

    closeX.addEventListener('click', removeOverlay);

    btnRow.appendChild(yesBtn);
    btnRow.appendChild(noBtn);
    dialog.appendChild(closeX);
    dialog.appendChild(title);
    dialog.appendChild(message);
    dialog.appendChild(btnRow);
    overlay.appendChild(dialog);

    docEditDiv.appendChild(overlay);
  };

  // Expose methods to the parent via ref
  useImperativeHandle(ref, () => ({
    handleDocumentExport,
  }));

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
  );
});
 
export default PdfViewerComponent;

